///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2025
//                                  RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { defer } from "lodash";

import BellIcon from "../icons/BellIcon";
import ExclamationIcon from "../icons/ExclamationIcon";
import ClogIcon from "../icons/ClogIcon";
import ReminderIcon from "../icons/ReminderIcon";
import { notificationTypes } from "../../helpers/types";
import formatDuration from "../../helpers/formatDuration";
import AccessTimeIcon from "../icons/AccessTimeIcon";
import {
  markSingleAsRead,
  downloadLaneErrorDocument,
  downloadLaneExportDocument,
  downloadAwardManagementExportDocument
} from "../../slices/notificationsSlice";
import Spinner from "../icons/Spinner";
import DownloadIcon from "../icons/DownloadIcon";
import closeNotificationFlyoutWindow from "../../helpers/closeNotificationFlyoutWindow";
import messages from "../../helpers/messages";
import redirectToBid from "../../helpers/redirectToBid";

const Icon = ({ notificationType, isFlyout, ...props }) => {
  const iconSize = isFlyout ? "32px" : "36px";
  return (
    <span className="inline-block icon full-height align-top">
      {notificationType === notificationTypes.userMessage && (
         <BellIcon
          title={messages.general.defaultMessage}
          height={iconSize}
          width={iconSize}
        />
      )}
      {notificationType === notificationTypes.general && (
        <BellIcon
          title={messages.general.defaultMessage}
          height={iconSize}
          width={iconSize}
        />
      )}
      {notificationType === notificationTypes.reminder && (
        <ReminderIcon height={iconSize} width={iconSize} />
      )}
      {notificationType === notificationTypes.systemMessage && (
        <ClogIcon
          title={messages.systemUpdates.defaultMessage}
          height={iconSize}
          width={iconSize}
        />
      )}
      {notificationType === notificationTypes.systemError && (
        <ExclamationIcon
          title={messages.systemError.defaultMessage}
          height={iconSize}
          width={iconSize}
        />
      )}
      {notificationType === notificationTypes.systemSuccess && (
        <ClogIcon
          title={messages.systemSuccess.defaultMessage}
          height={iconSize}
          width={iconSize}
        />
      )}
    </span>
  );
};

const DocumentButton = ({
  handleDownload,
  isDownloading,
  linkContent = messages.downloadErrorDocument.defaultMessage
}) => (
  <button
    onClick={handleDownload}
    className="pos-relative text-left mt-12 lane-upload-error-button no-focus"
    disabled={isDownloading}
  >
    {isDownloading ? (
      <Spinner
        className="spinner icon pos-absolute"
        height="12px"
        width="12px"
        fill="#0088D1"
      />
    ) : (
      <DownloadIcon
        className="pos-absolute icon"
        fill="#0088D1"
        height="16px"
        width="16px"
      />
    )}
    <span className="lane-error-download">{linkContent}</span>
  </button>
);

export const Notification = ({
  notification: {
    htmlContent,
    isPreviouslySeenByUser,
    createDateUTC,
    uploadId,
    ...rest
  },
  isFlyout
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const markAsRead = e => {
    e.preventDefault();
    e.persist();
    if (!isPreviouslySeenByUser) {
      if (e.target.classList.contains("bid-name") || isFlyout) {
        dispatch(
          markSingleAsRead({
            notificationId: rest.notificationId,
            bidId: rest.bidId,
            redirect: () => {
              if (rest.bidId) {
                redirectToBid(e, rest.bidId, history);
                defer(() => {
                  closeNotificationFlyoutWindow();
                });
              }
            }
          })
        );
      }
    } else {
      if (e.target.classList.contains("bid-name") || isFlyout) {
        closeNotificationFlyoutWindow();
        redirectToBid(e, rest.bidId, history);
      }
    }
  };

  const handleDownloadLaneErrorDocument = e => {
    e.stopPropagation();
    dispatch(
      downloadLaneErrorDocument({
        bidId: rest.bidId,
        notificationId: rest.notificationId,
        uploadId,
        fileName: rest.bidName + "-errors.xlsx"
      })
    );
  };

  const handleDownloadLaneExportDocument = e => {
    e.stopPropagation();
    dispatch(
      downloadLaneExportDocument({
        bidId: rest.bidId,
        notificationId: rest.notificationId,
        uploadId,
        fileName: rest.bidName + "-LaneExport.xlsx"
      })
    );
  };

  const handleDownloadAwardManagementExportDocument = e => {
    e.stopPropagation();
    dispatch(
      downloadAwardManagementExportDocument({
        bidId: rest.bidId,
        notificationId: rest.notificationId,
        uploadId,
        fileName: rest.bidName + "-AwardManagement.xlsx"
      })
    );
  };

  const showLaneImportError =
    uploadId && rest.notificationType === notificationTypes.systemError;
  const systemMessageTitle =
    rest.notificationType === notificationTypes.systemMessage &&
    messages.systemUpdates.defaultMessage;
  const isLaneExportDownloadNotification =
    rest.notificationType === notificationTypes.systemSuccess &&
    rest.notificationSubType === 31;
  const isAwardManagementExportDownloadNotification =
    rest.notificationType === notificationTypes.systemSuccess &&
    rest.notificationSubType === 30;

  return (
    <div
      onClick={markAsRead}
      className={`${isFlyout ? "flyout-notification" : "notification"}`}
    >
      {rest.markingAsRead ? (
        <Spinner
          className="spinner marking-read"
          height="12px"
          width="12px"
          fill="#0088D1"
        />
      ) : (
        <span
          className={"unread " + (isPreviouslySeenByUser ? "invisible" : "")}
        />
      )}
      <div className="ml-16 one-whole flex full-height">
        <Icon isFlyout={isFlyout} {...rest} />
        <div className="t80">
          <div
            className={
              "content align-middle one-whole flex items-center flex-wrap " +
              (!showLaneImportError && "full-height")
            }
          >
            {systemMessageTitle && (
              <span className="font-size-14 font-bold one-whole">
                {systemMessageTitle}
              </span>
            )}
            <span
              className="html-message one-whole"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            ></span>
            {showLaneImportError && (
              <DocumentButton
                handleDownload={handleDownloadLaneErrorDocument}
                isDownloading={rest.downloadingErrorDocument}
              />
            )}
            {isLaneExportDownloadNotification && (
              <DocumentButton
                linkContent={messages.downloadDocument.defaultMessage}
                handleDownload={handleDownloadLaneExportDocument}
                isDownloading={rest.downloadingLaneExportDocument}
              />
            )}
            {isAwardManagementExportDownloadNotification && (
              <DocumentButton
                linkContent={messages.downloadFOTemplate.defaultMessage}
                handleDownload={handleDownloadAwardManagementExportDocument}
                isDownloading={rest.downloadingAwardManagementExportDocument}
              />
            )}
          </div>
          <div className="time">
            {isFlyout && (
              <AccessTimeIcon
                className="align-middle mr-4"
                height={12}
                width={12}
                fill="#616161"
              />
            )}
            <FormattedMessage
              {...messages.timeSinceLastUpdateDurationAgo}
              values={{
                timeSinceLastUpdateDurationAgo: formatDuration(
                  createDateUTC + "Z"
                )
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notification;
